// Spacious card-style header for the household detail page.
// Per design decision: prioritize visual breathing room over compactness.
// Top row: breadcrumb + actions. Middle row: identity + advisor. Bottom row: stat cards.

import { Link } from 'react-router-dom';

function formatCurrency(value) {
  if (value == null || isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

const ADVISOR_LABELS = {
  'robert@bullogicwealth.com': 'Robert',
  'lisa@bullogictax.com':      'Lisa',
  'adam@bullogicwealth.com':   'Adam',
};
const DIVISION_LABELS = { bwm: 'BWM', bts: 'BTS', bia: 'BIA' };

export default function HouseholdHeader({ household, members, people = [], totals }) {
  const advisorName = household.advisor_email
    ? (ADVISOR_LABELS[household.advisor_email.toLowerCase()] || household.advisor_email)
    : 'Unassigned';

  // Subtitle lists people (humans + entities) — sourced from the people
  // table so renames in the People tab propagate here too. Falls back to
  // active engagement names if the people table is empty (defensive;
  // shouldn't happen post-Phase A.5).
  const personLabels = people.length > 0
    ? [...people].sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)).map(p => p.name)
    : members.filter(m => m.status === 'Active').map(m => m.name);
  const memberNames = personLabels.join(' · ');

  // Counts: "People" comes from the people table; engagements from members.
  // Separating the two clarifies that one human can have many engagements.
  const peopleCount = people.length;
  const activeEngagements = members.filter(m => m.status === 'Active').length;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-6">

      {/* Top row: back link + actions */}
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/crm/households"
          className="text-[11px] uppercase tracking-wider text-[#888] hover:text-bullogic-blue transition-colors flex items-center gap-1"
        >
          ← Households
        </Link>
        <div className="flex items-center gap-2">
          {/* Phase B: these are placeholder buttons. Phase C/D wires them up. */}
          <button
            className="text-[12px] text-[#888] px-3 py-1.5 border border-gray-200 rounded-[4px] hover:bg-bone hover:text-navy transition-colors cursor-not-allowed"
            disabled
            title="Coming in Phase D"
          >
            Push module
          </button>
          <button
            className="text-[12px] text-[#888] px-3 py-1.5 border border-gray-200 rounded-[4px] hover:bg-bone hover:text-navy transition-colors cursor-not-allowed"
            disabled
            title="Coming in Phase D"
          >
            Invite to portal
          </button>
          <button
            className="text-[12px] font-bold text-white px-3 py-1.5 bg-navy rounded-[4px] hover:bg-navy/90 transition-colors cursor-not-allowed"
            disabled
            title="Coming in Phase C"
          >
            Edit
          </button>
        </div>
      </div>

      {/* Middle row: identity + advisor */}
      <div className="flex items-start justify-between gap-8 mb-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-[28px] font-bold text-navy leading-tight mb-1">
            {household.name}
          </h1>
          {memberNames && (
            <p className="text-[13px] text-[#666] mb-3">{memberNames}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {totals.divisions.map(d => (
              <span
                key={d}
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-[2px] bg-bullogic-blue/10 text-bullogic-blue"
              >
                {DIVISION_LABELS[d] || d}
              </span>
            ))}
            {household.state && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-[2px] bg-deep-teal/10 text-deep-teal">
                {household.state}
              </span>
            )}
            {totals.active_count === 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-[2px] bg-rose/10 text-rose">
                No active members
              </span>
            )}
          </div>
        </div>

        {/* Advisor card */}
        <div className="flex-shrink-0 bg-bone rounded-[4px] px-4 py-3 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-wider text-[#888] mb-1">
            Assigned Advisor
          </div>
          <div className="text-[14px] font-bold text-navy">{advisorName}</div>
          {household.advisor_email && (
            <div className="text-[11px] text-[#888] truncate">{household.advisor_email}</div>
          )}
        </div>
      </div>

      {/* Bottom row: stat cards. "People" = human + entity count from the
          people table. "Active Engagements" = service-relationship rows
          from clients. The two used to be conflated as "Active Members"
          which was wrong post-Phase A.5 (one human can have many
          engagements). */}
      <div className="grid grid-cols-5 gap-3">
        <StatCard label="Total Annual Revenue" value={formatCurrency(totals.annual_revenue)} />
        <StatCard label="Total AUM"            value={formatCurrency(totals.aum)} />
        <StatCard label="People"               value={String(peopleCount)} />
        <StatCard label="Active Engagements"   value={`${activeEngagements}${totals.member_count > activeEngagements ? ` of ${totals.member_count}` : ''}`} />
        <StatCard label="Service Tier(s)" value={
          totals.service_tiers.length === 0 ? '—'
          : totals.service_tiers.map(t => t.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ')
        } small={totals.service_tiers.length > 1} />
      </div>
    </div>
  );
}

function StatCard({ label, value, small }) {
  return (
    <div className="bg-bone rounded-[4px] px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-[#888] mb-1">{label}</div>
      <div className={`font-bold text-navy ${small ? 'text-[13px]' : 'text-[18px]'}`}>{value}</div>
    </div>
  );
}
